/**
 * Presence des autres joueurs: lisser ce qui arrive par a-coups.
 *
 * Le reseau livre une position huit a dix fois par seconde, le rendu en veut
 * soixante. Poser le bonhomme du voisin la ou le dernier paquet l'a dit revient
 * a le TELEPORTER dix fois par seconde: il saccade, et ses pieds patinent parce
 * que l'animation ne voit plus une vitesse mais une suite de sauts.
 *
 * On affiche donc le passe: chaque joueur est dessine tel qu'il etait il y a
 * `RETARD_INTERPOLATION_MS`, ce qui laisse presque toujours un paquet DEVANT
 * l'instant demande et permet d'interpoler entre deux positions connues au lieu
 * d'extrapoler vers une position inventee. Cent millisecondes de retard ne se
 * voient pas; un bonhomme qui tressaute, si.
 *
 * Ce module est PUR: le temps arrive en argument. C'est ce qui permet de le
 * tester sans navigateur, et de le faire tourner sur le serveur le jour ou il
 * prendra l'autorite.
 */
import type { EtatPresence } from '../types/reseau.ts'

/** Retard d'affichage, en millisecondes. */
export const RETARD_INTERPOLATION_MS = 100

/**
 * Au-dela de ce silence, un joueur est considere comme parti.
 *
 * TRES large devant la periode de publication, et c'est voulu: ce delai n'est
 * qu'un filet. Un depart normal est annonce par le message `part` et prend
 * effet tout de suite; une liaison vraiment morte est reperee par le battement
 * de coeur du serveur. Ce qui reste ici, c'est le cas ou l'on ne saura jamais —
 * et dans ce cas, mieux vaut garder une seconde de trop quelqu'un qui rame que
 * faire clignoter un joueur qui est bien la.
 */
export const DELAI_SILENCE_MS = 10_000

/**
 * Nombre maximum d'autres joueurs DESSINES.
 *
 * Un bonhomme coute quatorze appels de dessin (un par segment). Le budget de la
 * scene complete est de 150 (voir tests/e2e/performance.spec.ts): quatre
 * voisins, soit 56 appels, laissent de la marge. Au-dela, les plus eloignes ne
 * sont pas dessines — ils restent connus, ils reapparaissent en s'approchant.
 */
export const MAX_AUTRES_AFFICHES = 4

/** Periode de publication de son propre etat, en millisecondes. */
export const PERIODE_PUBLICATION_MS = 120

/** Periode de redistribution par le serveur, en millisecondes. */
export const PERIODE_DIFFUSION_MS = 100

/**
 * Nombre d'instantanes gardes par joueur. Deux suffisent a interpoler; on en
 * garde de quoi absorber un paquet en retard sans perdre le fil.
 */
const MAX_INSTANTS = 12

interface Instant {
  t: number
  e: EtatPresence
}

/** Interpolation d'angle par le plus court chemin: pas de tour complet. */
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d < -Math.PI) d += Math.PI * 2
  return a + d * t
}

/** L'historique recent d'un joueur, et de quoi le lire a n'importe quel instant. */
export class PistePresence {
  private readonly instants: Instant[] = []

  /**
   * `t` est l'heure d'ARRIVEE mesuree par le recepteur, pas une heure envoyee
   * par l'emetteur: aucune horloge n'a besoin d'etre synchronisee entre les
   * machines, ce qui serait une autre affaire.
   */
  ajouter(t: number, e: EtatPresence): void {
    const dernier = this.instants[this.instants.length - 1]
    // Un paquet arrive dans le desordre n'a plus rien a apprendre: le suivant
    // l'a deja depasse. L'inserer ferait reculer le bonhomme.
    if (dernier && t <= dernier.t) return
    this.instants.push({ t, e })
    if (this.instants.length > MAX_INSTANTS) this.instants.shift()
  }

  get dernierT(): number {
    return this.instants[this.instants.length - 1]?.t ?? -Infinity
  }

  get vide(): boolean {
    return this.instants.length === 0
  }

  /**
   * L'etat a l'instant `t` (deja retarde par l'appelant).
   *
   * Avant le premier instant connu: le premier. Apres le dernier: le dernier,
   * TENU en place. On n'extrapole jamais — deviner un mouvement qui n'a pas eu
   * lieu fait avancer un bonhomme immobile puis le ramene en arriere.
   */
  echantillon(t: number): EtatPresence | null {
    const n = this.instants.length
    if (n === 0) return null
    const premier = this.instants[0] as Instant
    if (t <= premier.t) return premier.e
    const dernier = this.instants[n - 1] as Instant
    if (t >= dernier.t) return dernier.e

    for (let i = n - 1; i > 0; i--) {
      const b = this.instants[i] as Instant
      const a = this.instants[i - 1] as Instant
      if (t < a.t) continue
      const duree = b.t - a.t
      const f = duree > 0 ? (t - a.t) / duree : 1
      return {
        x: a.e.x + (b.e.x - a.e.x) * f,
        z: a.e.z + (b.e.z - a.e.z) * f,
        rot: lerpAngle(a.e.rot, b.e.rot, f),
        // Ce qui n'est pas continu ne s'interpole pas: on garde l'etat de
        // depart jusqu'a ce que l'instant suivant soit vraiment atteint,
        // sinon un joueur serait « a moitie dans une maison ».
        etat: a.e.etat,
        nage: a.e.nage,
        vehicule: a.e.vehicule,
        interieur: a.e.interieur,
        // L'etage est discret: interpoler entre 0 et 1 poserait le voisin a
        // mi-hauteur de son escalier, dans le plafond.
        niveau: a.e.niveau,
        appat: a.e.appat,
      }
    }
    return dernier.e
  }
}

/** Tous les autres joueurs, chacun avec sa piste. */
export class SuiviPresence {
  private readonly pistes = new Map<string, PistePresence>()
  private readonly retard: number

  constructor(retard: number = RETARD_INTERPOLATION_MS) {
    this.retard = retard
  }

  recevoir(id: string, t: number, e: EtatPresence): void {
    let p = this.pistes.get(id)
    if (!p) {
      p = new PistePresence()
      this.pistes.set(id, p)
    }
    p.ajouter(t, e)
  }

  oublier(id: string): void {
    this.pistes.delete(id)
  }

  vider(): void {
    this.pistes.clear()
  }

  get nombre(): number {
    return this.pistes.size
  }

  ids(): string[] {
    return [...this.pistes.keys()]
  }

  /** L'etat a afficher maintenant, retard compris. */
  etatDe(id: string, maintenant: number): EtatPresence | null {
    return this.pistes.get(id)?.echantillon(maintenant - this.retard) ?? null
  }

  /**
   * Oublie ceux dont on n'a plus de nouvelles et rend leur liste.
   *
   * Sans ca, un joueur dont le portable s'est endormi resterait plante au
   * milieu du monde jusqu'au prochain rechargement de page.
   */
  expirer(maintenant: number, silence: number = DELAI_SILENCE_MS): string[] {
    const partis: string[] = []
    for (const [id, p] of this.pistes) {
      if (maintenant - p.dernierT > silence) partis.push(id)
    }
    for (const id of partis) this.pistes.delete(id)
    return partis
  }
}

/**
 * Les `max` joueurs les plus proches d'un point.
 *
 * Le plafond d'affichage n'est pas arbitraire: c'est le budget de rendu (voir
 * MAX_AUTRES_AFFICHES). Choisir les PLUS PROCHES est ce qui rend la coupure
 * invisible — celui qu'on ne dessine pas est celui qu'on ne verrait pas.
 */
export function joueursLesPlusProches(
  positions: readonly { id: string; x: number; z: number }[],
  x: number,
  z: number,
  max: number = MAX_AUTRES_AFFICHES,
): string[] {
  return [...positions]
    .map((p) => ({ id: p.id, d: (p.x - x) * (p.x - x) + (p.z - z) * (p.z - z) }))
    // A egalite, l'ordre alphabetique: sans ce departage, deux joueurs a la
    // meme distance pouvaient echanger leur place a chaque image.
    .sort((a, b) => a.d - b.d || (a.id < b.id ? -1 : 1))
    .slice(0, Math.max(0, max))
    .map((p) => p.id)
}
